import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Analytics } from 'app/utils/analytics/analytics'
import { getTimezone } from 'app/utils/getTimezone'
import { useSessionContext } from 'app/utils/supabase/useSessionContext'
import { useSupabase } from 'app/utils/supabase/useSupabase'
import { useCallback, useEffect, useState } from 'react'
import { JSONObject } from 'superjson/dist/types'
import {
  DEFAULTS,
  getStoredPreferences,
  setStoredPreferences,
  UserPreferences,
} from './userStorage'

// Add default values in case import fails
const DEFAULT_VALUES = {
  DAY_START: '09:00',
  DAY_END: '17:00',
  PROACTIVE_FREQUENCY: 'off',
} as const

/**
 * Represents a user profile in the application.
 */
type Profile = {
  id: string
  name: string | null
  avatar_url: string | null
  about: string | null
  core_memory: JSONObject | string | null
  timezone: string | null
  day_start_time: string | null
  day_end_time: string | null
  phone: string | null
  proactive_outreach_frequency: string | null
  is_sms_notification_enabled: boolean | null
}

/**
 * Custom hook to fetch and manage the user's profile data.
 * @returns A query object containing the user's profile data and loading state.
 */
function useProfile() {
  const { session } = useSessionContext()
  const user = session?.user
  const supabase = useSupabase()

  return useQuery(['profile', user?.id], {
    queryFn: async () => {
      if (!user?.id) {
        return null
      }

      const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single()

      if (error) {
        // no rows - edge case of user being deleted
        if (error.code === 'PGRST116') {
          // User deleted - sign out directly using supabase without importing useAuth
          await supabase.auth.signOut({ scope: 'local' })
          return null
        }
        throw new Error(error.message)
      }

      let profileData = data as Profile

      // Defensive: Parse core_memory if it's a string (Supabase serialization bug)
      if (profileData?.core_memory && typeof profileData.core_memory === 'string') {
        try {
          const parsedCoreMemory = JSON.parse(profileData.core_memory)
          profileData = { ...profileData, core_memory: parsedCoreMemory }
        } catch (err) {
          console.error('[useProfile] Failed to parse core_memory:', err)
          profileData = { ...profileData, core_memory: null }
        }
      }

      if (profileData?.timezone) {
        setStoredPreferences({ timezone: profileData.timezone })
      }

      // Also store day start/end times if they exist
      if (profileData?.day_start_time || profileData?.day_end_time) {
        setStoredPreferences({
          dayStart: profileData.day_start_time || undefined,
          dayEnd: profileData.day_end_time || undefined,
        })
      }

      return profileData
    },
    enabled: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  })
}

/**
 * Custom hook to fetch and manage user profile data with optimized loading.
 *
 * Key features:
 * 1. Uses session storage for fast access to preferences (timezone, day start/end)
 * 2. Lazy loads profile data only when needed (query disabled by default)
 * 3. Maintains backward compatibility with existing components
 *
 * Loading strategy:
 * - Initial load uses stored preferences to avoid waiting for profile
 * - Only considers auth session loading as blocking
 * - Profile query is disabled but available via updateProfile()
 *
 * @returns {Object} User data, preferences, and loading states
 */
export const useUser = () => {
  const { session, isLoading: isLoadingSession } = useSessionContext()
  const user = session?.user

  // // Set analytics user ID when session changes
  useEffect(() => {
    if (user?.id) {
      Analytics.setUserId(user.id)
      // Set initial user properties
      Analytics.setUserProperties({
        email: user.email,
        // Add any other user properties we want to track
      })
    } else {
      // Clear user ID when logged out
      Analytics.setUserId(null)
    }
  }, [user?.id, user?.email])

  // Profile query is disabled by default (enabled: false)
  // Only used when explicitly needed via updateProfile()
  const { data: profile, refetch } = useProfile()

  // Add a function to manually load the profile
  const updateProfile = useCallback(() => {
    return refetch()
  }, [refetch])

  const queryClient = useQueryClient()
  const supabase = useSupabase()

  // Track loading state for preferences
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true)

  // Initialize with defaults, using fallback if DEFAULTS is undefined
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const defaultPrefs = {
      name: '',
      timezone: getTimezone(),
      dayStart: (DEFAULTS?.DAY_START ?? DEFAULT_VALUES.DAY_START) as string,
      dayEnd: (DEFAULTS?.DAY_END ?? DEFAULT_VALUES.DAY_END) as string,
      phone: '',
      proactiveFrequency: (DEFAULTS?.PROACTIVE_FREQUENCY ??
        DEFAULT_VALUES.PROACTIVE_FREQUENCY) as string,
      isSmsNotificationEnabled: false,
    }

    return defaultPrefs
  })

  // Load stored preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const stored = await getStoredPreferences()

        if (stored) {
          const updatedPrefs = {
            name: stored.name || preferences.name,
            timezone: stored.timezone || preferences.timezone,
            dayStart: stored.dayStart || preferences.dayStart,
            dayEnd: stored.dayEnd || preferences.dayEnd,
            phone: stored.phone || preferences.phone,
            proactiveFrequency: stored.proactiveFrequency || preferences.proactiveFrequency,
            isSmsNotificationEnabled:
              stored.isSmsNotificationEnabled ?? preferences.isSmsNotificationEnabled,
          }

          setPreferences(updatedPrefs)
        }
      } finally {
        setIsLoadingPreferences(false)
      }
    }
    loadPreferences()
  }, [])

  // Sync preferences when profile changes
  // This ensures storage stays in sync with server data
  useEffect(() => {
    if (profile) {
      const newPrefs = {
        name: profile.name || preferences.name,
        timezone: profile.timezone || preferences.timezone,
        dayStart: profile.day_start_time || preferences.dayStart,
        dayEnd: profile.day_end_time || preferences.dayEnd,
        phone: profile.phone || preferences.phone,
        proactiveFrequency: profile.proactive_outreach_frequency || preferences.proactiveFrequency,
        isSmsNotificationEnabled:
          profile.is_sms_notification_enabled || preferences.isSmsNotificationEnabled,
      }

      setPreferences(newPrefs)
      setStoredPreferences(newPrefs)
    }
  }, [
    profile?.name,
    profile?.timezone,
    profile?.day_start_time,
    profile?.day_end_time,
    profile?.phone,
    profile?.proactive_outreach_frequency,
    profile?.is_sms_notification_enabled,
  ])

  // Handle timezone updates
  // Updates both local storage and server if user is logged in
  const setUserTimezone = useCallback(
    async (newTimezone: string) => {
      // Update local state and storage immediately
      setPreferences((prev) => ({ ...prev, timezone: newTimezone }))
      setStoredPreferences({ timezone: newTimezone })

      // Sync with server if user is logged in
      if (user) {
        await queryClient.fetchQuery(['profile', user.id], async () => {
          const { error } = await supabase
            .from('profiles')
            .update({ timezone: newTimezone } as Partial<Profile>)
            .eq('id', user.id)

          if (error) {
            console.error('Error updating timezone in profile:', error)
            throw error
          }
          return { ...profile, timezone: newTimezone } as Profile
        })
      }
    },
    [user, profile, queryClient, supabase]
  )

  // Update isLoading to include preferences loading state
  const isLoading = isLoadingSession || isLoadingPreferences

  // Return synchronized preferences and minimal loading state
  // isLoading only depends on session since we use stored preferences
  const returnValue = {
    session,
    user,
    profile, // Available but may be null (query disabled)
    name: preferences.name,
    timezone: preferences.timezone,
    dayStart: preferences.dayStart,
    dayEnd: preferences.dayEnd,
    updateProfile, // Use our new function with logging
    isLoadingSession,
    isLoadingProfile: false, // Always false since query is disabled
    isLoading, // Only block on session loading
    setTimezone: setUserTimezone,
  }

  return returnValue
}

/**
 * Hook for updating the user's name.
 * @returns A mutation object for updating the user's name.
 */
// export const useUpdateUserName = () => {
//   const supabase = useSupabase()
//   const queryClient = useQueryClient()
//   const { session } = useSessionContext()
//   const user = session?.user

//   return useMutation({
//     mutationFn: async (name: string) => {
//       if (!user) throw new Error('No user found')
//       await supabase.from('profiles').update({ name }).eq('id', user.id)
//     },
//     onSuccess: () => {
//       if (user) {
//         queryClient.invalidateQueries(['profile', user.id])
//       }
//     },
//   })
// }

/**
 * Hook for updating the user's profile.
 * @returns A mutation object for updating the user's profile.
 */
export const useUpdateUserProfile = () => {
  const supabase = useSupabase()
  const queryClient = useQueryClient()
  const { session } = useSessionContext()
  const user = session?.user

  return useMutation({
    mutationFn: async (profileData: Partial<Profile>) => {
      if (!user) throw new Error('No user found')

      let finalData = profileData

      // If core_memory is present in the update data
      if ('core_memory' in profileData) {
        // First fetch the current profile
        const { data: currentProfile } = await supabase
          .from('profiles')
          .select<string, Profile>('core_memory')
          .eq('id', user.id)
          .single()

        const existingCoreMemory = currentProfile?.core_memory as any
        const isExistingEmpty =
          !existingCoreMemory ||
          (typeof existingCoreMemory === 'object' && Object.keys(existingCoreMemory).length === 0)

        if (!isExistingEmpty) {
          // Remove core_memory from update if existing is not empty
          const { core_memory, ...dataWithoutCoreMemory } = profileData
          finalData = dataWithoutCoreMemory
          console.log('core_memory removed from update - existing not empty', existingCoreMemory)
        } else {
          console.log('core_memory updated - existing was empty', {
            existing: existingCoreMemory,
            new: profileData.core_memory,
          })
        }
      }

      // Single update call with .select() to get updated data
      const { data, error } = await supabase
        .from('profiles')
        .update(finalData)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      return data as Profile
    },
    onSuccess: (data) => {
      if (user) {
        // Update the query cache with the new data
        queryClient.setQueryData(['profile', user.id], (oldData: Profile | undefined) => {
          if (!oldData) return data
          // Preserve existing data and only update the changed fields
          return {
            ...oldData,
            ...data,
            // Ensure core_memory is preserved if it wasn't in the update
            core_memory: data.core_memory ?? oldData.core_memory,
          }
        })
      }
    },
  })
}

/**
 * Hook for accessing timezone-related functions.
 * @returns An object containing getTimezone and setTimezone functions.
 */
export const useTimezone = () => {
  const { timezone, setTimezone } = useUser()
  return { timezone, setTimezone }
}

export const useUpdateEmail = () => {
  const supabase = useSupabase()
  const queryClient = useQueryClient()
  const { session } = useSessionContext()
  const user = session?.user

  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const { data, error } = await supabase.auth.updateUser({ email })
      if (error) {
        throw error
      }
      return data
    },
    onSuccess: () => {
      if (user) {
        queryClient.invalidateQueries(['user', user.id])
      }
    },
  })
}

export const useUpdatePassword = () => {
  const supabase = useSupabase()

  return useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        throw error
      }
    },
  })
}
